// slider-captcha.component.ts

import { ICaptchaService, TrajectoryPoint } from '../interface/captcha.interface';

export class SliderCaptchaComponent {
    private isDragging = false;
    private startX = 0;
    private currentX = 0;
    private trajectory: TrajectoryPoint[] = [];
    private lastRecordTime = 0;
    private captchaId: string = '';
    private expectedPosition: number = 0;

    private maxPosition: number = 330;

    // Реальные размеры картинки на экране
    private actualImageWidth: number = 420;
    private actualImageHeight: number = 200;


    private readonly BASE_WIDTH: number = 420;

    // Статус капчи
    private isCaptchaPassed: boolean = false;
    private captchaLockTime: number = 0;
    private readonly CAPTCHA_LOCK_DURATION = 50 * 60 * 1000; // 50 минут
    private unlockTimer: ReturnType<typeof setTimeout> | null = null;
    private isVerifying: boolean = false;

    private sliderTrack: HTMLElement;
    private sliderThumb: HTMLElement;
    private captchaImage: HTMLImageElement;
    private captchaPiece: HTMLImageElement;
    private pieceContainer: HTMLElement;
    private hintElement: HTMLElement;
    private captchaContainer: HTMLElement;

    constructor(
      private container: HTMLElement,
      private captchaService: ICaptchaService,
      private onCaptchaReady: () => void,
      private onCaptchaError: (message: string) => void
    ) {
        this.sliderTrack = container.querySelector('[data-slider-track]')!;
        this.sliderThumb = container.querySelector('[data-slider-thumb]')!;
        this.captchaImage = container.querySelector('[data-captcha-image]')!;
        this.captchaPiece = container.querySelector('[data-captcha-piece]')!;
        this.pieceContainer = container.querySelector('[data-piece-container]')!;
        this.hintElement = container.querySelector('[data-slider-captcha-hint]')!;
        this.captchaContainer = container.closest('[data-slider-captcha]')!;

        this.maxPosition = this.BASE_WIDTH - 90;


        this.init();
    }

    public async refreshCaptcha(): Promise<void> {
        if (this.isCaptchaPassed && !this.isCaptchaExpired()) {
            return;
        }
        await this.loadNewCaptcha();
    }

    public lockCaptchaExternally(): void {
        this.captchaImage.src = `/assets/images/captcha/success.png`;
        this.captchaPiece.src = `data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==`;

        if (this.isCaptchaPassed) {
            return;
        }

        if (this.isVerifying) {
            return;
        }

        this.saveCaptchaPassed();
        this.lockCaptcha();
        this.startUnlockTimer();
    }

    public async loadNewCaptcha(): Promise<void> {
        if (this.isCaptchaPassed && !this.isCaptchaExpired()) {
            this.lockCaptcha();
            this.startUnlockTimer();
            return;
        }

        if (this.isCaptchaPassed && this.isCaptchaExpired()) {
            this.resetCaptchaStatus();
        }

        try {
            this.isVerifying = false;
            this.captchaService.resetCaptchaData();

            const response = await fetch('/captcha/slider/new');
            const data = await response.json();

            this.captchaId = data.captcha_id;
            this.expectedPosition = data.position;

            this.captchaImage.src = `/captcha/slider/${this.captchaId}?t=${Date.now()}`;
            this.captchaPiece.src = `/captcha/slider/piece/${this.captchaId}?t=${Date.now()}`;

            await Promise.all([
                new Promise((resolve) => {
                    this.captchaImage.onload = () => {
                        this.updateActualImageSize();
                        resolve(true);
                    };
                }),
                new Promise((resolve) => {
                    this.captchaPiece.onload = resolve;
                })
            ]);

            this.pieceContainer.style.display = 'block';
            this.pieceContainer.style.pointerEvents = 'none';

            this.updatePiecePosition(0);
            this.resetSlider();

            this.sliderThumb.style.pointerEvents = 'auto';
            this.sliderTrack.style.opacity = '1';
            this.pieceContainer.style.opacity = '1';
            this.sliderThumb.style.backgroundColor = '';
            this.captchaContainer.classList.remove('captcha-passed');

            this.updateHint('Потяните ползунок, чтобы совместить пазл', '');

        } catch (error) {
            console.error('Failed to load captcha:', error);
            this.onCaptchaError('Не удалось загрузить каптчу. Обновите страницу.');
        }
    }

    // ============ ПРИВАТНЫЕ МЕТОДЫ ============

    private async init(): Promise<void> {


        if (this.isCaptchaPassed && !this.isCaptchaExpired()) {
            this.lockCaptcha();
            this.startUnlockTimer();
            return;
        }

        if (this.isCaptchaPassed && this.isCaptchaExpired()) {
            this.resetCaptchaStatus();
        }

        await this.loadNewCaptcha();
        this.bindEvents();

        window.addEventListener('resize', () => {
            this.updateActualImageSize();
            this.updatePiecePosition(this.currentX);
        });
    }

    private bindEvents(): void {
        this.sliderThumb.addEventListener('mousedown', this.onDragStart.bind(this));
        document.addEventListener('mousemove', this.onDragMove.bind(this));
        document.addEventListener('mouseup', this.onDragEnd.bind(this));

        this.sliderThumb.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
        document.addEventListener('touchcancel', this.onTouchCancel.bind(this), { passive: false });
    }
    private onTouchCancel(e: TouchEvent): void {
        if (!this.isDragging) return;
        e.preventDefault();
        this.isDragging = false;
        this.sliderThumb.style.cursor = 'grab';
        this.resetSlider();
    }


    private isCaptchaExpired(): boolean {
        return Date.now() - this.captchaLockTime > this.CAPTCHA_LOCK_DURATION;
    }

    private getRemainingLockTime(): number {
        const elapsed = Date.now() - this.captchaLockTime;
        return Math.max(0, this.CAPTCHA_LOCK_DURATION - elapsed);
    }

    private lockCaptcha(): void {
        this.captchaContainer.classList.add('captcha-passed');
        this.sliderThumb.style.pointerEvents = 'none';
        this.sliderTrack.style.opacity = '0.5';
        this.pieceContainer.style.opacity = '0.5';
        this.sliderThumb.style.backgroundColor = '#4caf50';

        const remainingMinutes = Math.ceil(this.getRemainingLockTime() / 60000);
        this.updateHint(`Капча пройдена (разблокируется через ${remainingMinutes} мин)`, 'green');

        this.pieceContainer.style.display = 'block';
        this.pieceContainer.style.pointerEvents = 'none';
    }

    public unlockCaptcha(): void {
        this.isDragging = true;
        this.captchaContainer.classList.remove('captcha-passed');
        this.sliderThumb.style.pointerEvents = 'auto';
        this.sliderTrack.style.opacity = '1';
        this.pieceContainer.style.opacity = '1';
        this.sliderThumb.style.backgroundColor = '';

        this.isCaptchaPassed = false;
        this.captchaLockTime = 0;

        this.updateHint('Потяните ползунок, чтобы совместить пазл', '');
    }

    private resetCaptchaStatus(): void {
        this.isCaptchaPassed = false;
        this.captchaLockTime = 0;

        if (this.unlockTimer) {
            clearTimeout(this.unlockTimer);
            this.unlockTimer = null;
        }
    }

    private saveCaptchaPassed(): void {
        this.isCaptchaPassed = true;
        this.captchaLockTime = Date.now();
    }

    private startUnlockTimer(): void {
        if (this.unlockTimer) {
            clearTimeout(this.unlockTimer);
            this.unlockTimer = null;
        }

        const remainingTime = this.getRemainingLockTime();

        if (remainingTime > 0) {
            this.unlockTimer = setTimeout(() => {
                this.unlockCaptcha();
                this.loadNewCaptcha();
            }, remainingTime);
        } else {
            this.unlockCaptcha();
            this.loadNewCaptcha();
        }
    }

    private updateHint(text: string, className: string = ''): void {
        if (this.hintElement) {
            this.hintElement.textContent = text;
            this.hintElement.className = `slider-captcha__hint ${className}`;
        }
    }

    // ============ МЕТОДЫ ОБНОВЛЕНИЯ РАЗМЕРОВ ============

    private updateActualImageSize(): void {
        if (this.captchaImage) {
            this.actualImageWidth = this.captchaImage.offsetWidth;
            this.actualImageHeight = this.captchaImage.offsetHeight;
        }
    }

    private calculateServerPosition(): number {
        const maxDelta = this.getMaxDelta();
        const positionPercentage = maxDelta > 0 ? this.currentX / maxDelta : 0;


        let serverPosition = positionPercentage * this.maxPosition;


        if (this.actualImageWidth > 0 && this.actualImageWidth !== this.BASE_WIDTH) {
            const scaleFactor = this.actualImageWidth / this.BASE_WIDTH;
            serverPosition = serverPosition * scaleFactor;
        }

        return Math.round(serverPosition);
    }

    private updateSliderPosition(x: number): void {
        this.sliderThumb.style.transform = `translateX(${x}px)`;
    }


    private updatePiecePosition(x: number): void {
        const maxDelta = this.getMaxDelta();
        const percentage = maxDelta > 0 ? x / maxDelta : 0;


        let pieceX = percentage * this.maxPosition;


        if (this.actualImageWidth > 0 && this.actualImageWidth !== this.BASE_WIDTH) {
            const scaleFactor = this.actualImageWidth / this.BASE_WIDTH;
            pieceX = pieceX * scaleFactor;
        }

        this.captchaPiece.style.transform = `translateX(${pieceX}px)`;
    }

    private getMaxDelta(): number {
        const trackRect = this.sliderTrack.getBoundingClientRect();
        const thumbRect = this.sliderThumb.getBoundingClientRect();
        return trackRect.width - thumbRect.width;
    }

    private onDragStart(e: MouseEvent): void {
        e.preventDefault();
        this.startDrag(e.clientX);
    }

    private onTouchStart(e: TouchEvent): void {
        e.preventDefault();
        this.startDrag(e.touches[0].clientX);
    }

    private startDrag(clientX: number): void {
        if (this.isVerifying || this.isCaptchaPassed) {
            return;
        }

        this.isDragging = true;
        this.startX = clientX;
        this.trajectory = [];
        this.lastRecordTime = Date.now();
        this.recordTrajectory(0, this.getRandomYOffset());
        this.sliderThumb.style.cursor = 'grabbing';
    }

    private onDragMove(e: MouseEvent): void {
        if (!this.isDragging) return;
        e.preventDefault();
        this.dragMove(e.clientX);
    }

    private onTouchMove(e: TouchEvent): void {
        if (!this.isDragging) return;
        e.preventDefault();
        this.dragMove(e.touches[0].clientX);
    }

    private dragMove(clientX: number): void {
        const deltaX = clientX - this.startX;
        const maxDelta = this.getMaxDelta();
        let newX = Math.max(0, Math.min(deltaX, maxDelta));
        this.currentX = newX;

        this.updateSliderPosition(newX);
        this.updatePiecePosition(newX);
        this.recordTrajectory(newX, this.getRandomYOffset());
    }


    private async onDragEnd(): Promise<void> {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.sliderThumb.style.cursor = 'grab';

        this.recordTrajectory(this.currentX, this.getRandomYOffset());
        const maxDelta = this.getMaxDelta();
        const positionPercentage = maxDelta > 0 ? this.currentX / maxDelta : 0;
        const expectedPercentage = this.expectedPosition / this.maxPosition;
        const isMobile = this.isMobile();
        const isCorrect = Math.abs(positionPercentage - expectedPercentage) < 0.08;

        if (isCorrect && this.trajectory.length >= (isMobile ? 3 : 5)) {
            this.sliderThumb.style.backgroundColor = '#4caf50';
            this.pieceContainer.style.transition = 'opacity 0.3s';
            this.pieceContainer.style.opacity = '0.7';

            const serverPosition = this.calculateServerPosition();

            this.captchaService.setSliderCaptchaData(
              this.captchaId,
              serverPosition,
              this.trajectory,
              this.actualImageWidth,
              this.actualImageHeight
            );

            this.isVerifying = true;
            this.sliderThumb.style.pointerEvents = 'none';
            this.updateHint('Проверка...', 'verifying');

            this.onCaptchaReady();

        } else {
            this.sliderThumb.style.backgroundColor = '#f44336';
            const errorMessage = this.trajectory.length < (isMobile ? 3 : 5)
              ? 'Переместите ползунок плавно до конца'
              : 'Совместите пазл на картинке';
            this.updateHint('');
            this.onCaptchaError(errorMessage);

            setTimeout(() => {
                this.loadNewCaptcha();
            }, 1000);
        }
    }

    private onTouchEnd(e: TouchEvent): void {
        this.onDragEnd();
    }

    private isMobile(): boolean {
        return /iPhone|iPad|iPod|Android|BlackBerry|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    }

    private getRandomYOffset(): number {
        const isMobile = this.isMobile();
        if (isMobile) {
            return (Math.random() - 0.5) * 4;
        }
        return Math.sin(Date.now() / 50) * (Math.random() * 3) + (Math.random() - 0.5) * 5;
    }

    private recordTrajectory(x: number, y: number): void {
        const now = Date.now();
        const isMobile = this.isMobile();
        const interval = isMobile ? 20 : 15;

        if (now - this.lastRecordTime >= interval) {
            this.trajectory.push({
                x: Math.round(x),
                y: Math.round(y + 50),
                timestamp: now
            });
            this.lastRecordTime = now;
        }
    }

    private resetSlider(): void {
        this.currentX = 0;
        this.updateSliderPosition(0);
        this.updatePiecePosition(0);
        this.trajectory = [];
        this.sliderThumb.style.backgroundColor = '';
        this.sliderThumb.style.cursor = 'grab';
        this.pieceContainer.style.opacity = '1';
        this.isVerifying = false;
    }
}
